import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Box, Button, Grid, Tab, Tabs, TextField, Typography } from '@mui/material';
import grey from '@mui/material/colors/grey';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { Prompt } from 'react-router';

import { DataAPIContext, UPDATE } from '../api';
import AccessTagsDropdown from '../components/AccessTagsDropdown';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import { imageURL } from '../components/images';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import { a11yProps } from '../components/TabPanel';
import UsedByPanel from '../components/UsedByPanel';
import { ContextStore } from '../store/contextStore';
import { ConfirmationDialogText } from './utils';

const useStyles = makeStyles((theme) => ({
    root: {
        border: `1px solid ${grey[200]}`,
        borderRadius: '5px',
        marginTop: '1rem',
    },
    gridRoot: {
        overflowY: 'auto',
        overflowX: 'hidden',
        height: 'calc(100svh - 17rem)',
        width: '100%',
        boxSizing: 'border-box',
        padding: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            height: 'calc(100svh - 20rem)',
            padding: theme.spacing(1),
        },
    },
    buttonPanel: {
        marginTop: theme.spacing(4),
        backgroundColor: grey[100],
    },
}));

const getComparableState = (cat) => {
    const a = { ...cat };
    delete a._changed;
    return JSON.stringify(a);
};

const ActivityCategoryEdit = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'activity_category';
    const classes = useStyles();
    const [syncActive, setSyncActive] = React.useState(false);
    const { hasPerm, settings } = React.useContext(ContextStore);
    const { enqueueSnackbar } = useSnackbar();
    const [category, setCategory] = React.useState({ ...props.data, _changed: false });
    const [iconFile, setIconFile] = React.useState(null);
    const [iconPreview, setIconPreview] = React.useState(props.data?.icon ? imageURL(settings.images.base, props.data.icon, 'cover_88x88') : null);
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [currentTab, setCurrentTab] = React.useState(0);

    const hasWritePerm = hasPerm('activity', 'write');

    const onLicenseeChange = (id) => {
        setCategory((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };

    if (category.id !== props.data.id) {
        setCategory({ ...props.data, _changed: false });
        setIconFile(null);
        setIconPreview(props.data?.icon ? imageURL(settings.images.base, props.data.icon, 'cover_88x88') : null);
    }

    const originalState = getComparableState(props.data);
    const currentState = getComparableState(category);
    const isChanged = originalState !== currentState || !!iconFile;

    const handleChange = (ev) => {
        setCategory((prev) => ({ ...prev, [ev.target.name]: ev.target.value, _changed: true }));
    };

    const onAccessTagsChange = (ev, atags) => {
        setCategory((prev) => ({ ...prev, atag: atags, _changed: true }));
    };

    const handleIconChange = (ev) => {
        const file = ev.target.files?.[0];
        if (file) {
            setIconFile(file);
            setIconPreview(URL.createObjectURL(file));
            setCategory((prev) => ({ ...prev, _changed: true }));
        }
    };

    const handleSave = () => {
        if (!category.title) return;
        setSyncActive(true);
        (async () => {
            try {
                const payload = { title: category.title, atag: category.atag };
                if (iconFile) {
                    payload.image = iconFile;
                }
                const res = await dataAPI(UPDATE, resource, { id: category.id, data: payload, upload: true });
                if (res.statusCode === 200) {
                    enqueueSnackbar('Activity category updated', { variant: 'success' });
                    setIconFile(null);
                    setCategory({ ...res.data, _changed: false });
                    setIconPreview(res.data?.icon ? imageURL(settings.images.base, res.data.icon, 'cover_88x88') : null);
                    props.onChange();
                } else {
                    enqueueSnackbar(res.data?.message || 'Error updating category', { variant: 'error' });
                }
            } finally {
                setSyncActive(false);
            }
        })();
    };

    const doDelete = () => {
        props.onDelete && props.onDelete(props.data);
    };

    const handleDelete = () => {
        setConfirmationDialogAction('delete_activity_category');
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };

    const confirmationDialogConfirm = () => {
        if (confirmationDialogAction === 'delete_activity_category') {
            doDelete();
        }
        confirmationDialogCancel();
    };

    const canSave = !!category.title && hasWritePerm;

    return (
        <FormLicenseeContext.Provider value={toLicenseeId(category.tour_operator)}>
        <div className={classes.root}>
            <PageMeta title={`Edit Category - ${category.title}`} />
            <Prompt when={!!category._changed && hasWritePerm} message="Are you sure you want to leave without saving changes?" />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <FixedLinearProgress visibility={syncActive} />
            <Grid container direction="row" spacing={2}>
                <Grid item xs={12} container direction="row" alignItems="center" alignContent="center">
                    <Grid item xs={8} p={2} pb={0}>
                        <Typography variant="h6">Category Details</Typography>
                    </Grid>
                    <Grid item xs={4} container justifyContent="flex-end" alignItems="center">
                        <Button aria-label="close-preview" onClick={props.onClose}>
                            <CloseIcon />
                        </Button>
                    </Grid>
                </Grid>
                {category.id && <Grid item xs={12}>
                    <Tabs value={currentTab} onChange={(ev, newTab) => setCurrentTab(newTab)} indicatorColor="primary" textColor="primary">
                        <Tab label="General" {...a11yProps(0)} />
                        <Tab label="Used by" {...a11yProps(1)} />
                    </Tabs>
                </Grid>}
                {currentTab === 0 && (
                    <Grid item xs={12} sx={{ minWidth: 0, width: '100%' }}>
                    <Box className={classes.gridRoot}>
                    <Grid container direction="row" spacing={2}>
                        <Grid item xs={12}>
                            <LicenseeFormField value={category.tour_operator} onChange={onLicenseeChange} disabled={!hasWritePerm} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                disabled={!hasWritePerm}
                                required
                                variant="filled"
                                size="small"
                                fullWidth
                                name="title"
                                label="Category Title"
                                value={category.title || ''}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Button variant="outlined" component="label" size="small" disabled={!hasWritePerm}>
                                    {iconPreview ? 'Change Icon' : 'Upload Icon'}
                                    <input type="file" hidden accept="image/*" onChange={handleIconChange} />
                                </Button>
                                {iconPreview && <Avatar src={iconPreview} variant="rounded" sx={{ width: 40, height: 40 }} />}
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <AccessTagsDropdown disabled={!hasWritePerm} value={category.atag || []} onChange={onAccessTagsChange} />
                        </Grid>
                    </Grid>
                    </Box>
                    </Grid>
                )}
                {category.id && currentTab === 1 && (
                    <Grid item xs={12} sx={{ minWidth: 0, width: '100%' }}>
                        <Box className={classes.gridRoot}>
                            <UsedByPanel resource={resource} id={category.id} layout="narrow" />
                        </Box>
                    </Grid>
                )}
            </Grid>
            <Box p={1} className={classes.buttonPanel}>
                <FormActionButtons
                    mode="edit"
                    onSubmit={handleSave}
                    submitDisabled={!canSave || !isChanged}
                    onDelete={handleDelete}
                    deletePerm="activity.delete"
                    onCancel={props.onClose}
                />
            </Box>
        </div>
        </FormLicenseeContext.Provider>
    );
};

export default ActivityCategoryEdit;
